import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { queryKeys } from "../../api/queryKeys";
import { meSchema } from "../../api/schemas";
import type { Me } from "../../api/schemas";
import { useOffline } from "../../offline/OfflineContext";
import { useAuth } from "../auth/AuthContext";
import { createRoleSnapshot } from "./roleSnapshot";
import { useTenantSelectionStore } from "./tenantSelection";

/**
 * Loads the profile of the signed-in person (`GET /me`). Every successful answer also stores the
 * roles as the last known roles of this person and tenant on the device, for the navigation after
 * an app start without connection (F-05, ADR-0104); a failing local store never fails the query.
 *
 * @returns The query result.
 */
export function useMe(): UseQueryResult<Me> {
  const client = useApiClient();
  const { service } = useAuth();
  const tenants = useTenantSelectionStore();
  const { store, nowMs } = useOffline();
  const query = useQuery({
    queryKey: queryKeys.me,
    queryFn: async ({ signal }) => {
      const fetchedAtMs = nowMs();
      const me = await client.get("/api/v1/me", meSchema, { signal });
      const remember = async (): Promise<void> => {
        const user = await service.getUser();

        if (user === null) {
          return;
        }

        const snapshot = createRoleSnapshot(
          me,
          user.subject,
          tenants.get(user.subject),
          fetchedAtMs,
        );

        if (snapshot !== null) {
          await store.putRoleSnapshot(snapshot);
        }
      };
      // Not awaited: a blocked or failing IndexedDB must not delay or fail the profile.
      remember().catch(() => undefined);

      return me;
    },
    staleTime: 60_000,
  });

  return query;
}
